import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Input } from '@/components/ui/input';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { useProductOrigins } from '@/hooks/warehouse/useProductOrigins';
import FormModal from '@/components/v2/molecules/admin/shared/FormModal';
import { useEffect } from 'react';

const formSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
});

type FormData = z.infer<typeof formSchema>;

interface ProductOriginFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  originId?: string | null;
  initialName?: string;
  onSuccess?: () => void;
}

export default function ProductOriginFormModal({
  open,
  onOpenChange,
  originId,
  initialName,
  onSuccess
}: ProductOriginFormModalProps) {
  const { createOrigin, updateOrigin, isCreating, isUpdating } = useProductOrigins();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name: initialName || '',
      });
    }
  }, [open, initialName, form]);

  const onSubmit = async (data: FormData) => {
    try {
      if (originId) {
        await updateOrigin({ id: originId, name: data.name });
      } else {
        await createOrigin({ name: data.name });
      }
      if (onSuccess) onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving origin:', error);
    }
  };

  const isLoading = isCreating || isUpdating;

  return (
    <FormModal
      isOpen={open}
      onClose={() => onOpenChange(false)}
      title={originId ? 'Editar Origen' : 'Nuevo Origen'}
      form={form}
      onSubmit={onSubmit}
      submitLabel={originId ? 'Guardar' : 'Crear'}
      isLoading={isLoading}
      maxWidth="max-w-[400px]"
    >
      <FormField
        control={form.control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Nombre</FormLabel>
            <FormControl>
              <Input placeholder="Ej: IKEA, Walmart..." {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </FormModal>
  );
}
